// src/iap/initPurchases.ts
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import * as appActions from '../Actions/app';
import { reportError } from 'Utils/reportError';
declare global {
  interface Window {
    CdvPurchase?: CdvPurchaseApi;
    cordova?: { exec?: CordovaExec };
    Capacitor: unknown;
    __iapInitialized?: boolean;
    __iapSetupPromise?: Promise<CdvPurchaseApi | undefined>;
    __iapLastVerifiedReceiptHash?: string;
    __iapLastVerifiedReceiptActive?: boolean;
    __iapVerifyArmedUntil?: number;
    __iapVerifyArmedProductId?: string;
    __iapLastProductLoadErrors?: CdvStoreError[];
  }
}

type MinimalUser = { id?: string | number; user_id?: string; email?: string | null } | null;
type Dispatch = (a: unknown) => void;
type CordovaExec = (
  success: (res: unknown) => void,
  error: (e: unknown) => void,
  service: string,
  action: string,
  args: unknown[],
) => void;

export type CdvStoreError = {
  isError?: boolean;
  code?: string | number;
  message?: string;
  productId?: string | null;
};

export type CdvPurchaseOffer = {
  productId: string;
  platform: unknown;
  order?: (additionalData?: unknown) => Promise<CdvStoreError | undefined>;
  canPurchase?: boolean;
};

export type CdvPurchaseProduct = {
  id: string;
  canPurchase?: boolean;
  getOffer?: (id?: string) => CdvPurchaseOffer | undefined;
};

export type CdvPurchaseStore = {
  verbosity?: unknown;
  minTimeBetweenUpdates?: number;
  applicationUsername?: () => string;
  register: (products: unknown[]) => void;
  when: () => { approved: (cb: (tx: CdvPurchaseTransaction) => void | Promise<void>) => void };
  error: (cb: (e: unknown) => void) => void;
  ready: (cb: () => void) => void;
  initialize: (platforms: unknown[]) => Promise<unknown>;
  update: () => Promise<unknown>;
  get: (id: string, platform?: unknown) => CdvPurchaseProduct | undefined;
  getReceipt?: (platform: unknown) => Promise<unknown>;
  order: (offer: CdvPurchaseOffer, additionalData?: unknown) => Promise<CdvStoreError | undefined>;
};

export type CdvPurchaseApi = {
  store: CdvPurchaseStore;
  ProductType: { PAID_SUBSCRIPTION: unknown };
  Platform: { APPLE_APPSTORE: unknown; GOOGLE_PLAY: unknown };
  LogLevel?: { DEBUG: unknown };
};

type GooglePlayNativePurchase = {
  acknowledged?: boolean;
  orderId?: string;
  packageName?: string;
  productId?: string;
  productIds?: string[];
  purchaseTime?: number;
  purchaseToken?: string;
  receipt?: string;
  signature?: string;
};

type CdvPurchaseTransaction = {
  products?: { id: string }[];
  receipt?: unknown;
  nativePurchase?: GooglePlayNativePurchase;
  parentReceipt?: unknown;
  transactionId?: string;
  originalTransactionId?: string;
  platform?: unknown;
  finish: () => void | Promise<void>;
};

const LOG = '[IAP]';
const log = (...a: unknown[]) => console.log(LOG, ...a);
const warn = (...a: unknown[]) => console.warn(LOG, ...a);
const err = (...a: unknown[]) => console.error(LOG, ...a);

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const IAP_VERIFY_ARM_WINDOW_MS = 15 * 60 * 1000;
const IAP_PLUGIN_WAIT_TIMEOUT_MS = 10 * 1000;
const IAP_PLUGIN_WAIT_INTERVAL_MS = 100;
const CORDOVA_DEVICE_READY_TIMEOUT_MS = 10 * 1000;

export const APPLE_IAP_PRODUCT_IDS = [
  'com.museflow.monthly',
  'com.museflow.3months',
  'com.museflow.annual',
] as const;

export const ANDROID_IAP_PRODUCT_IDS = [
  process.env.REACT_APP_ANDROID_IAP_MONTHLY_PRODUCT_ID || 'com.museflow.monthly',
  process.env.REACT_APP_ANDROID_IAP_3_MONTH_PRODUCT_ID || 'com.museflow.3months',
  process.env.REACT_APP_ANDROID_IAP_ANNUAL_PRODUCT_ID || 'com.museflow.annual',
] as const;

export type NativeIAPStorePlatform = 'apple' | 'google';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isStoreError = (value: unknown): value is CdvStoreError => {
  const error = value as CdvStoreError;
  return (
    !!error && typeof error === 'object' && (!!error.message || !!error.code || !!error.isError)
  );
};

function storeErrorsFrom(result: unknown): CdvStoreError[] {
  if (Array.isArray(result)) return result.filter(isStoreError);
  return isStoreError(result) ? [result] : [];
}

function getCdvPurchase(): CdvPurchaseApi | undefined {
  const Cdv = window.CdvPurchase;
  if (
    Cdv?.store &&
    Cdv?.Platform?.APPLE_APPSTORE &&
    Cdv?.Platform?.GOOGLE_PLAY &&
    Cdv?.ProductType?.PAID_SUBSCRIPTION
  ) {
    return Cdv;
  }
  return undefined;
}

export function nativeIAPStorePlatform(): NativeIAPStorePlatform | undefined {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'apple';
  if (platform === 'android') return 'google';
  return undefined;
}

export function iapStoreDisplayName(platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? 'Apple' : 'Google Play';
}

function nativeStorePlatform(Cdv: CdvPurchaseApi, platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
}

function iapProductIds(platform: NativeIAPStorePlatform) {
  return platform === 'apple' ? APPLE_IAP_PRODUCT_IDS : ANDROID_IAP_PRODUCT_IDS;
}

async function waitForCdvPurchase(
  timeoutMs: number = IAP_PLUGIN_WAIT_TIMEOUT_MS,
): Promise<CdvPurchaseApi | undefined> {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const Cdv = getCdvPurchase();
    if (Cdv) return Cdv;
    await delay(IAP_PLUGIN_WAIT_INTERVAL_MS);
  }
  return getCdvPurchase();
}

export function waitForDeviceReady(timeoutMs: number = CORDOVA_DEVICE_READY_TIMEOUT_MS) {
  if (!window.cordova) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let settled = false;
    const timeout = { id: undefined as number | undefined };
    const finish = () => {
      if (settled) return;
      settled = true;
      document.removeEventListener('deviceready', finish);
      if (timeout.id !== undefined) window.clearTimeout(timeout.id);
      resolve();
    };

    // Cordova's deviceready event is sticky: when it has already fired,
    // addEventListener invokes the listener synchronously. Define the timer
    // binding first and avoid scheduling one after that immediate resolution.
    document.addEventListener('deviceready', finish, { once: true });
    if (settled) return;

    timeout.id = window.setTimeout(() => {
      if (!settled) {
        reportError(
          new Error(`'deviceready' not fired within ${timeoutMs}ms; proceeding without it`),
          'iap.deviceReady',
        );
      }
      finish();
    }, timeoutMs);
  });
}

export function armIAPReceiptVerification(productId: string) {
  const expiresAt = Date.now() + IAP_VERIFY_ARM_WINDOW_MS;
  window.__iapVerifyArmedUntil = expiresAt;
  window.__iapVerifyArmedProductId = productId;
  log('verify arm set for product', productId, 'until', new Date(expiresAt).toISOString());
}

export function clearIAPReceiptVerificationArm() {
  window.__iapVerifyArmedUntil = undefined;
  window.__iapVerifyArmedProductId = undefined;
}

function isReceiptVerificationArmed(productIds: string[]): boolean {
  const armedUntil = window.__iapVerifyArmedUntil ?? 0;
  if (!armedUntil) return false;

  if (Date.now() > armedUntil) {
    log('verify arm expired, clearing arm');
    clearIAPReceiptVerificationArm();
    return false;
  }

  const armedProductId = window.__iapVerifyArmedProductId;
  if (armedProductId && productIds.length > 0 && !productIds.includes(armedProductId)) {
    log('verify arm mismatch, skipping verify', {
      armedProductId,
      approvedProductIds: productIds,
    });
    return false;
  }

  return true;
}

const looksB64 = (s: unknown): s is string =>
  typeof s === 'string' && s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s);

function pickB64(obj: unknown): string | undefined {
  if (!obj) return;
  if (looksB64(obj)) return obj;
  const keys = [
    'appStoreReceipt',
    'appStoreReceiptBase64',
    'latestReceipt',
    'receipt',
    'receipt_data',
    'base64',
    'data',
    'raw',
  ];
  const o = obj as Record<string, unknown>;
  for (const k of keys) if (looksB64(o?.[k])) return o[k] as string;
  for (const v of Object.values(o)) if (looksB64(v)) return v as string;
  return;
}

// Tiny cheap hash for dedupe (NOT cryptographic; just for “don’t spam verify”).
function cheapHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

function getAppleAppReceiptViaExec(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const exec = window?.cordova?.exec;
    if (!exec) {
      warn('cordova.exec not available');
      return resolve(undefined);
    }
    log('exec → InAppPurchase.appStoreReceipt');
    exec(
      (res: unknown) => {
        const b64 = pickB64(res);
        log('exec ← appStoreReceipt:', b64 ? `ok(len=${b64.length})` : `no-b64`);
        resolve(b64);
      },
      (e: unknown) => {
        warn('exec appStoreReceipt failed', e);
        resolve(undefined);
      },
      'InAppPurchase',
      'appStoreReceipt',
      [],
    );
  });
}

async function getAppleReceiptB64(Cdv: CdvPurchaseApi): Promise<string | undefined> {
  const { store, Platform } = Cdv;
  // Preferred: v13+ getter
  if (typeof store?.getReceipt === 'function') {
    try {
      const r = await store.getReceipt(Platform.APPLE_APPSTORE);
      const b64 = pickB64(r);
      log('store.getReceipt →', b64 ? `ok(len=${b64.length})` : 'no-b64');
      if (b64) return b64;
    } catch (e) {
      warn('store.getReceipt threw', e);
    }
  } else {
    log('store.getReceipt is undefined (JS bundled incorrectly or old adapter).');
  }
  // Fallback: call native directly
  return getAppleAppReceiptViaExec();
}

type IAPVerifyPayload =
  | {
      platform: 'apple';
      receipt_data: string;
      reason?: 'approved';
      tx_id?: string;
      original_tx_id?: string;
    }
  | {
      platform: 'google';
      product_id: string;
      product_ids: string[];
      purchase_token: string;
      package_name?: string;
      order_id?: string;
      receipt_data?: string;
      signature?: string;
      reason?: 'approved';
      tx_id?: string;
      purchase_time?: number;
      acknowledged?: boolean;
    };

type GooglePurchasePayload = Extract<IAPVerifyPayload, { platform: 'google' }>;

function verifyPayloadDedupeKey(payload: IAPVerifyPayload) {
  if (payload.platform === 'apple') return `apple:${cheapHash(payload.receipt_data)}`;
  return `google:${payload.purchase_token || cheapHash(JSON.stringify(payload))}`;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const readString = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const readNumber = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'number' ? value : undefined;
};

const readBoolean = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return typeof value === 'boolean' ? value : undefined;
};

const readStringArray = (source: Record<string, unknown> | undefined, key: string) => {
  const value = source?.[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
};

function googlePurchasePayloadFromTransaction(
  tx: CdvPurchaseTransaction,
): GooglePurchasePayload | undefined {
  const nativePurchase = asRecord(tx.nativePurchase);
  const parentReceipt = asRecord(tx.parentReceipt);
  const receipt = asRecord(tx.receipt);
  const productIds = [
    ...readStringArray(nativePurchase, 'productIds'),
    ...((tx.products || []).map((p) => p.id).filter(Boolean) as string[]),
  ];
  const productId =
    productIds[0] || readString(nativePurchase, 'productId') || (tx.products || [])[0]?.id;
  const purchaseToken =
    readString(nativePurchase, 'purchaseToken') ||
    readString(parentReceipt, 'purchaseToken') ||
    readString(receipt, 'purchaseToken');

  if (!productId || !purchaseToken) return undefined;

  return {
    platform: 'google',
    product_id: productId,
    product_ids: Array.from(new Set(productIds.length > 0 ? productIds : [productId])),
    purchase_token: purchaseToken,
    package_name: readString(nativePurchase, 'packageName') || readString(receipt, 'packageName'),
    order_id:
      readString(nativePurchase, 'orderId') ||
      readString(parentReceipt, 'orderId') ||
      readString(receipt, 'orderId'),
    receipt_data: readString(nativePurchase, 'receipt') || readString(receipt, 'receipt'),
    signature: readString(nativePurchase, 'signature') || readString(receipt, 'signature'),
    reason: 'approved',
    tx_id: tx.transactionId,
    purchase_time:
      readNumber(nativePurchase, 'purchaseTime') ?? readNumber(receipt, 'purchaseTime'),
    acknowledged:
      readBoolean(nativePurchase, 'acknowledged') ?? readBoolean(receipt, 'acknowledged'),
  };
}

async function verify(
  payload: IAPVerifyPayload,
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<boolean> {
  try {
    const token = (await currentAuthToken?.()) || '';
    if (!token) {
      warn('verify skipped: no auth token');
      return false;
    }

    // Dedupe per receipt blob / Google purchase token.
    const h = verifyPayloadDedupeKey(payload);
    if (window.__iapLastVerifiedReceiptHash === h) {
      log('verify skipped: same receipt hash already verified');
      return !!window.__iapLastVerifiedReceiptActive;
    }

    const url = `${baseUrl}receipts/verify`;
    const { data, status } = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    log(
      'verify ←',
      status,
      'platform=',
      payload.platform,
      'active=',
      !!data?.active,
      'env=',
      data?.environment,
    );
    window.__iapLastVerifiedReceiptHash = h;
    window.__iapLastVerifiedReceiptActive = !!data?.active;

    if (data?.active) {
      dispatch(
        appActions.setUserData({
          ...(userData || {}),
          subscription_status: 'active',
        }),
      );
      return true;
    } else {
      log('verify returned inactive receipt; skipping local subscription mutation');
      return false;
    }
  } catch (e: unknown) {
    const errObj = e as { response?: { status?: number }; message?: string };
    err('verify failed', errObj?.response?.status, errObj?.message);
    reportError(e, 'iap.verify');
    return false;
  }
}

export function setupIAP(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
) {
  const platform = nativeIAPStorePlatform();
  if (!platform) {
    warn('setupIAP skipped: no native store platform for', Capacitor.getPlatform());
    return Promise.resolve(undefined);
  }

  if (window.__iapInitialized) {
    log('already initialized');
    return Promise.resolve(getCdvPurchase());
  }

  if (window.__iapSetupPromise) {
    log('initialization already in progress');
    return window.__iapSetupPromise;
  }

  window.__iapSetupPromise = (async () => {
    const Cdv = await waitForCdvPurchase();
    if (!Cdv) {
      err('CdvPurchase.store missing after waiting for Cordova purchase plugin');
      reportError(
        new Error(
          `CdvPurchase.store missing after waiting ${IAP_PLUGIN_WAIT_TIMEOUT_MS}ms for Cordova purchase plugin`,
        ),
        'iap.pluginWait',
      );
      return undefined;
    }

    await waitForDeviceReady();

    const { store, ProductType, LogLevel } = Cdv;
    const storePlatform = nativeStorePlatform(Cdv, platform);
    const productIds = iapProductIds(platform);
    try {
      store.verbosity = LogLevel?.DEBUG;
    } catch {
      // intentionally ignore
    }

    store.applicationUsername = () =>
      (userData?.id ?? userData?.user_id ?? userData?.email ?? '') + '';

    store.register(
      productIds.map((id) => ({
        id,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: storePlatform,
        group: 'museflow',
      })),
    );

    store.when().approved(async (tx: CdvPurchaseTransaction) => {
      const txProductIds = (tx?.products || []).map((p) => p.id);
      let shouldFinishTransaction = platform === 'apple';
      log('approved:', {
        txId: tx?.transactionId,
        orig: tx?.originalTransactionId,
        plat: tx?.platform,
        products: txProductIds,
      });

      try {
        const verificationArmed = isReceiptVerificationArmed(txProductIds);
        if (!verificationArmed && platform === 'apple') {
          log('approved: skipping verify (not armed by user purchase action)');
        } else {
          if (!verificationArmed) {
            log('approved: verifying Google Play purchase surfaced by store update');
          }
          let verifiedActive = false;

          if (platform === 'apple') {
            // Prefer receipt attached to tx, else pull app receipt.
            let b64 = pickB64(tx?.receipt);
            log('approved: tx.receipt present?', !!b64);
            if (!b64) b64 = await getAppleReceiptB64(Cdv);

            if (b64) {
              verifiedActive = await verify(
                {
                  platform: 'apple',
                  receipt_data: b64,
                  reason: 'approved',
                  tx_id: tx?.transactionId,
                  original_tx_id: tx?.originalTransactionId,
                },
                userData,
                dispatch,
                currentAuthToken,
              );
            } else {
              warn('approved: no app receipt available to verify');
            }
          } else {
            const googlePayload = googlePurchasePayloadFromTransaction(tx);
            if (googlePayload) {
              verifiedActive = await verify(googlePayload, userData, dispatch, currentAuthToken);
            } else {
              warn('approved: no Google purchase token available to verify');
            }
          }

          shouldFinishTransaction = platform === 'apple' || verifiedActive;
          clearIAPReceiptVerificationArm();
        }
      } finally {
        if (shouldFinishTransaction) {
          try {
            await tx.finish();
            log('approved: finish()');
          } catch (e) {
            err('finish() failed', e);
          }
        } else {
          warn('approved: finish() skipped until Google verification succeeds');
        }
        try {
          await store.update();
          log('approved: store.update()');
        } catch {
          // intentionally ignore
        }
      }
    });

    store.error((e: unknown) => err('Store Error:', e));
    store.ready(() => log('Store ready'));
    window.__iapInitialized = true;

    try {
      log(`initialize([${iapStoreDisplayName(platform)}])`);
      const initializeResult = await store.initialize([storePlatform]);
      const initializeErrors = storeErrorsFrom(initializeResult);
      if (initializeErrors.length > 0) {
        window.__iapLastProductLoadErrors = initializeErrors;
        warn('initialize returned product load errors', initializeErrors);
      }
      log('force product refresh after initialize');
      await forceIAPProductRefresh(Cdv);

      // IMPORTANT: removed cold-start verification
      // We only verify on actual approved transactions.
      log('startup: skipping verify (only verifying on purchase approval)');
    } catch (e) {
      err('initialize/update failed', e);
    }

    return Cdv;
  })().finally(() => {
    window.__iapSetupPromise = undefined;
  });

  return window.__iapSetupPromise;
}

export async function ensureIAPReady(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
): Promise<CdvPurchaseApi | undefined> {
  const Cdv = await setupIAP(userData, dispatch, currentAuthToken);
  if (!Cdv) return undefined;

  try {
    await forceIAPProductRefresh(Cdv);
  } catch (e) {
    warn('ensureIAPReady: product refresh failed', e);
  }

  return Cdv;
}

export function appleProductIdForPlan(planSelect: string) {
  return iapProductIdForPlan(planSelect, 'apple');
}

export function iapProductIdForPlan(
  planSelect: string,
  platform: NativeIAPStorePlatform = nativeIAPStorePlatform() || 'apple',
) {
  const productIds = iapProductIds(platform);
  return planSelect === 'monthly'
    ? productIds[0]
    : planSelect === '3-months'
      ? productIds[1]
      : planSelect === 'annual'
        ? productIds[2]
        : '';
}

export function appleProductStates(Cdv: CdvPurchaseApi) {
  return iapProductStates(Cdv, 'apple');
}

export function iapProductStates(
  Cdv: CdvPurchaseApi,
  platform: NativeIAPStorePlatform = nativeIAPStorePlatform() || 'apple',
) {
  const { store } = Cdv;
  const storePlatform = nativeStorePlatform(Cdv, platform);
  return iapProductIds(platform).map((id) => {
    const product = store.get(id, storePlatform);
    return {
      id,
      returned: !!product,
      canPurchase: !!product?.canPurchase,
    };
  });
}

export async function forceIAPProductRefresh(Cdv: CdvPurchaseApi) {
  const store = Cdv.store as CdvPurchaseStore & { lastUpdate?: number };
  const previousMinTimeBetweenUpdates = store.minTimeBetweenUpdates;

  try {
    store.minTimeBetweenUpdates = 0;
    store.lastUpdate = 0;
    const updateResult = await store.update();
    const updateErrors = storeErrorsFrom(updateResult);
    if (updateErrors.length > 0) {
      window.__iapLastProductLoadErrors = updateErrors;
      warn('product refresh returned errors', updateErrors);
    }
  } finally {
    store.minTimeBetweenUpdates = previousMinTimeBetweenUpdates;
  }
}
